import React, { useEffect, useMemo } from "react";
import get from "lodash/get";
import reduce from "lodash/reduce";
import sortBy from "lodash/sortBy";
import toLower from "lodash/toLower";

import styled from "styled-components";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid2";

import Button from "../shared/Button";
import TextFieldController from "../forms/TextFieldController";
import DatePickerController from "../forms/DatePickerController";
import AutocompleteController from "../forms/AutocompleteController";
import useOffices from "../hooks/useOffices";
import { getDefaultOffices, getStaticDocTypes } from "../../helpers/utils";
import { filter, includes, map, size } from "lodash";
import { useWatch } from "react-hook-form";

const DOCUMENT_TYPES = getStaticDocTypes();

const UploadInformation = ({
  file,
  nextStep,
  setData,
  handleCancel,
  control,
  handleSubmit,
  setValue,
}) => {
  const { data: officesData } = useOffices();

  useEffect(() => {
    if (size(officesData) !== 0) {
      const defaultOffice = getDefaultOffices(officesData);
      setValue(
        "offices",
        map(defaultOffice, (office) => office.id)
      );
    }
  }, [officesData]);

  const { officesOptions, officesById } = useMemo(() => {
    const officesOptions = sortBy(officesData, (item) =>
      toLower(get(item, "name"))
    );
    const officesById = reduce(
      officesOptions,
      (acc, curr) => {
        if (!acc[curr.id]) {
          acc[curr.id] = curr;
        }
        return acc;
      },
      {}
    );
    return {
      officesOptions,
      officesById,
    };
  }, [officesData]);

  const submitFn = (data) => {
    setData(data);
    nextStep();
  };

  return (
    <StyleWrapper>
      <Stack gap={3}>
        <Typography fontSize={26} fontWeight={700} color="var(--text-color)">
          Détails du document
        </Typography>
        <Stack
          sx={{
            borderBottom: "1px solid var(--text-color)",
            paddingBottom: "15px",
          }}
          direction="row"
        >
          <Typography
            fontSize={14}
            fontWeight={400}
            color="var(--text-color)"
            sx={{ marginRight: "10px" }}
          >
            Lorem ipsum dolor sit amet consectetur. Viverra pharetra velit eget
            morbi lorem sit vestibulum. Pulvinar mollis tristique justo est
            integer et at. Tellus leo feugiat rhoncus velit enim cursus eget
            libero pulvi.
          </Typography>
          <Box
            display="flex"
            alignItems="center"
            flexShrink={0}
            gap={2}
            sx={{
              border: "1px solid #1118270D",
              borderRadius: "6px",
              height: "52px",
              padding: "15px 20px",
              maxWidth: "270px",
            }}
          >
            <img
              className="upload-file-icon"
              src="/assets/icons/file-item.svg"
              alt=""
            />
            <Typography
              className="text-ellipsis"
              fontSize={14}
              fontWeight={500}
              color="var(--text-color)"
            >
              {file.content.name}
            </Typography>
          </Box>
        </Stack>
      </Stack>
      <Stack
        component="form"
        onSubmit={handleSubmit(submitFn)}
        gap={3}
        sx={{ marginTop: "20px" }}
      >
        <UploadInformationForm control={control} />
        <Grid size={{ xs: 12, md: 12 }}>
          <label className="form-label">Bureaux</label>
          <AutocompleteController
            control={control}
            name="offices"
            options={officesOptions}
            optionsById={officesById}
          />
        </Grid>
        <Stack direction="row" justifyContent="center" gap={2}>
          <Button className="cancel-btn" size="small" onClick={handleCancel}>
            Annuler
          </Button>
          <Button
            type="submit"
            className="next-btn"
            size="small"
            color="success"
            variant="contained"
          >
            Suivant
          </Button>
        </Stack>
      </Stack>
    </StyleWrapper>
  );
};

export const UploadInformationForm = ({ control, isRow = true }) => {
  const documentTypesById = useMemo(
    () =>
      reduce(DOCUMENT_TYPES, (acc, curr) => ({ ...acc, [curr.id]: curr }), {}),
    []
  );

  return (
    <Grid
      className="upload-info-form"
      container
      spacing={2}
      direction={isRow ? "row" : "column"}
    >
      <Grid size={{ xs: 12, md: isRow ? 5 : 12 }}>
        <label className="form-label">Nom du document</label>
        <TextFieldController
          name="name"
          control={control}
          placeholder="Ex : 20250101-Compromis de vente-Nom du bien"
          type="text"
          size="small"
        />
      </Grid>
      <Grid size={{ xs: 12, md: isRow ? 4 : 12 }}>
        <label className="form-label">Type du document</label>
        <AutocompleteController
          multiple={false}
          control={control}
          name="documentType"
          options={DOCUMENT_TYPES}
          optionsById={documentTypesById}
          size="small"
        />
      </Grid>
      <Grid size={{ xs: 12, md: isRow ? 3 : 12 }}>
        <label className="form-label">Date effective</label>
        <DatePickerController
          name="effectiveDate"
          control={control}
          slotProps={{
            textField: {
              size: "small",
              sx: {
                display: "block",
              },
              fullWidth: true,
            },
          }}
        />
      </Grid>
    </Grid>
  );
};

const StyleWrapper = styled.div`
  padding: 25px;
  min-width: 913px;

  .upload-file-icon {
    height: 20px;
    width: 16px;
  }

  .cancel-btn {
    width: 111px;
  }

  .next-btn {
    width: 270px;
  }
`;

export default UploadInformation;
